import { Directive, ElementRef, HostListener, Input } from '@angular/core';

@Directive({
  selector: '[appNumberFormat]',
  standalone: true,
})
export class NumberFormatDirective {
  @Input() isNonEditable: boolean = false;

  private el: HTMLInputElement;

  constructor(private elementRef: ElementRef) {
    this.el = this.elementRef.nativeElement;
  }

  @HostListener('input', ['$event']) onInputChange(event: Event): void {
    if (this.isNonEditable) return;

    const input = event.target as HTMLInputElement;
    const originalValue = input.value;
    const cursorPosition = input.selectionStart || 0;

    // Remove all formatting to get raw number
    let unformattedValue = originalValue.replace(/(?!^-)[^0-9+\-]/g, '').replace(/^-0+/, '-').replace(/^0+/, '');

    // Allow 0 only if it's a single digit
    if (unformattedValue === '0') {
      this.el.value = '0';
      return;
    }

    // Capture character before cursor in raw value
    const rawBeforeCursor = originalValue.slice(0, cursorPosition);
    const unformattedBeforeCursor = rawBeforeCursor.replace(/(?!^-)[^0-9+\-]/g, '');

    const caretIndexInRaw = unformattedBeforeCursor.length;

    // Format the value
    const formattedValue = this.formatNumber(unformattedValue);

    this.el.value = formattedValue;

    // Now map raw caret index to formatted string
    let formattedIndex = 0, rawIndex = 0;
    while (formattedIndex < formattedValue.length && rawIndex < caretIndexInRaw) {
      const char = formattedValue[formattedIndex];
      if (/\d/.test(char)) {
        rawIndex++;
      }
      formattedIndex++;
    }

    // Ensure cursor is within bounds
    formattedIndex = Math.max(0, Math.min(formattedValue.length, formattedIndex));
    input.setSelectionRange(formattedIndex, formattedIndex);
  }

  /*@HostListener('input', ['$event']) onInputChange(event: Event): void {
    if (this.isNonEditable) return;

    const input = event.target as HTMLInputElement;
    const cursorPosition = input.selectionStart || 0; // Save cursor position

    // Remove non-numeric characters except minus sign, plus sign, space, and leading zeros
    let unformattedValue = input.value.replace(/(?!^-)[^0-9+\-]/g, '').replace(/^-0+/, '-').replace(/^0+/, '');
    
    // Allow 0 only if it's a single digit
    if (unformattedValue === '0') {
      this.el.value = '0';
      return;
    }

    if (unformattedValue.length > 0) {
      const formattedValue = this.formatNumber(unformattedValue);

      // Update the input value with formatting
      this.el.value = formattedValue;

      // Restore cursor position
      // Calculate new cursor position after formatting
      const diff = formattedValue.length - unformattedValue.length;
      let newCursorPosition = cursorPosition + diff;

      // Ensure cursor position stays within bounds
      newCursorPosition = Math.max(0, Math.min(formattedValue.length, newCursorPosition));
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    } else {
      // Handle case where input becomes empty
      this.el.value = '0';
    }
  }*/

  @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Enter') {
        event.preventDefault(); // Prevent default behavior
        event.stopPropagation(); // Stop event from bubbling up
      }
    }


  @HostListener('keydown.enter', ['$event']) onEnterPress(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    const match = value.match(/([-+]?\d{1,3}(?:\.\d{3})*)/g);
    if (match) {
      let newValue = 0;
      match.forEach((num) => {
        newValue += parseFloat(num.replace(/\./g, ''));
      });

      this.el.value = this.formatNumber(newValue.toString());
      input.dispatchEvent(new Event('input')); // Manually trigger input event
    }
    // Move to the next input field
    const form = input.form;
    if (form) {
      const inputs = Array.from(form.querySelectorAll('input'));
      const currentIndex = inputs.indexOf(input);
      for (let i = currentIndex + 1; i < inputs.length; i++) {
        const nextInput = inputs[i] as HTMLInputElement;
        if (!nextInput.disabled) {
          nextInput.focus();
          break;
        }
      }
    }
  }

    @HostListener('keydown.tab', ['$event']) onTabPress(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    const match = value.match(/([-+]?\d{1,3}(?:\.\d{3})*)/g);
    if (match) {
      let newValue = 0;
      match.forEach((num) => {
        newValue += parseFloat(num.replace(/\./g, ''));
      });

      this.el.value = this.formatNumber(newValue.toString());
      input.dispatchEvent(new Event('input')); // Manually trigger input event
    }
  }

  @HostListener('keydown.arrowup', ['$event']) onArrowUp(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    const match = value.match(/([-+]?\d{1,3}(?:\.\d{3})*)/g);
    if (match) {
      let newValue = 0;
      match.forEach((num) => {
        newValue += parseFloat(num.replace(/\./g, ''));
      });

      this.el.value = this.formatNumber(newValue.toString());
      input.dispatchEvent(new Event('input'));
    }

    // Move to the previous input field
    const form = input.form;
    if (form) {
      const inputs = Array.from(form.querySelectorAll('input'));
      const currentIndex = inputs.indexOf(input);
      for (let i = currentIndex - 1; i >= 0; i--) {
        const prevInput = inputs[i] as HTMLInputElement;
        if (!prevInput.disabled) {
          prevInput.focus();
          break;
        }
      }
    }
  }

  @HostListener('keydown.arrowdown', ['$event']) onArrowDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    const match = value.match(/([-+]?\d{1,3}(?:\.\d{3})*)/g);
    if (match) {
      let newValue = 0;
      match.forEach((num) => {
        newValue += parseFloat(num.replace(/\./g, ''));
      });

      this.el.value = this.formatNumber(newValue.toString());
      input.dispatchEvent(new Event('input'));
    }

    // Move to the next input field
    const form = input.form;
    if (form) {
      const inputs = Array.from(form.querySelectorAll('input'));
      const currentIndex = inputs.indexOf(input);
      for (let i = currentIndex + 1; i < inputs.length; i++) {
        const nextInput = inputs[i] as HTMLInputElement;
        if (!nextInput.disabled) {
          nextInput.focus();
          break;
        }
      }
    }
  }

  @HostListener('blur', ['$event']) onBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    // Keep only the number, minus sign, and dots for thousand separators
    //const match = value.match(/^-?\d+(\.\d{3})*/);
    /*if (match) {
      this.el.value = match[0];
    } else {
      this.el.value = '';
    }
    // Trigger input event to ensure value is updated
    input.dispatchEvent(new Event('input'));*/
    const match = value.match(/([-+]?\d{1,3}(?:\.\d{3})*)/g);
    if (match) {
      let newValue = 0;
      match.forEach((num) => {
        newValue += parseFloat(num.replace(/\./g, ''));
      });

      this.el.value = this.formatNumber(newValue.toString());
      input.dispatchEvent(new Event('input')); // Manually trigger input event
    }
  }

  @Input() set formattedValue(value: number | string) {
    if (value != null) {
      const formatted = this.formatNumber(value.toString());
      this.el.value = formatted;
    }
  }

  private formatNumber(value: string): string {
    const parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.'); // Add thousand separators to the integer part
    return parts.join('.');
  }
}
